import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckIcon } from 'lucide-react';
import { TransformCta } from '../TransformCta';
import { IMAGES } from '../../lib/constants';
type Tomo = {
  number: string;
  title: string;
  description: string;
  bullets?: string[];
};
const TOMOS: Tomo[] = [{
  number: 'I',
  title: 'Arquitectura del Rostro',
  description: 'Aprende a detectar en minutos las proporciones faciales que determinan el éxito o el fracaso de cualquier plan de tratamiento.'
}, {
  number: 'II',
  title: 'El Perfil que Define el Tratamiento',
  description: 'Descubre cómo una correcta lectura del perfil puede evitar decisiones equivocadas como extracciones innecesarias o expansiones mal indicadas.'
}, {
  number: 'III',
  title: 'El Secreto de una Sonrisa Estética',
  description: 'Aprende a identificar los detalles que realmente hacen que un resultado ortodóncico sea percibido como armónico y atractivo por el paciente.'
}, {
  number: 'IV',
  title: 'La Cefalometría al Servicio del Rostro',
  description: 'Deja de depender únicamente de los números y aprende a utilizarlos para confirmar decisiones clínicas con mayor seguridad.',
  bullets: ['Integra el análisis facial con la cefalometría de forma lógica.', 'Identifica qué mediciones realmente influyen en tus decisiones.', 'Aprende cuándo confiar en el rostro antes que en el trazado cefalométrico.']
}, {
  number: 'V',
  title: 'Casos Complejos sin Incertidumbre',
  description: 'Aprende a reconocer con seguridad cuándo un caso puede resolverse con ortodoncia y cuándo requiere un enfoque quirúrgico.',
  bullets: ['Identifica los límites biológicos de la compensación ortodóncica.', 'Evalúa pacientes Clase III y alteraciones verticales con mayor confianza.', 'Evita planes que terminan en retratamientos o resultados poco predecibles.']
}, {
  number: 'VI',
  title: 'El Protocolo Final de Decisión Clínica',
  description: 'Sigue un sistema de 15 minutos para validar tu diagnóstico antes de iniciar cualquier tratamiento.',
  bullets: ['Revisa cada punto crítico antes de aprobar un plan.', 'Confirma que tus decisiones sean coherentes con la anatomía facial del paciente.', 'Presenta el tratamiento con mayor seguridad y respaldo clínico.']
}];
export function Contents() {
  return <section id="contenido" className="w-full bg-cream px-4 py-14 sm:py-20" data-id="element-117" data-mp="153nw8h7to1a0">
      <div className="mx-auto max-w-3xl" data-id="element-118" data-mp="155qkavpezkc3">
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-gold-600" data-id="element-119" data-mp="1a27ikiqbvf2w">
          Todo lo que incluye
        </p>
        <h2 className="mt-3 text-center font-display text-[26px] font-bold leading-[1.15] tracking-tight text-navy-900 sm:text-4xl" data-id="element-120" data-mp="1f3er4k0aoiam">
          Descubre todo lo que incluye el Sistema de Diagnóstico Facial
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-center text-[15px] leading-relaxed text-navy-600 sm:text-[17px]" data-id="element-121" data-mp="2qj3evdldhlch">
          No es un ebook. Es un sistema diseñado para acompañarte desde la primera observación del
          paciente hasta la validación final del plan de tratamiento.
        </p>

        <img src={IMAGES.mockup} alt="Los 6 tomos y 5 bonos del Sistema de Diagnóstico Facial" className="mx-auto mt-6 w-full max-w-xl" loading="lazy" data-id="element-122" data-mp="51r4vhfm93o3" />

        <div className="mt-10 rounded-3xl bg-navy-800 px-5 py-6 text-white sm:px-7" data-id="element-123" data-mp="3s6gt1s83dq58">
          <p className="font-display text-[19px] font-bold leading-snug sm:text-2xl" data-id="element-124" data-mp="3n85kdktj9lol">
            📦 Todo lo que recibirás al acceder hoy
          </p>
          <p className="mt-3 text-[14.5px] leading-relaxed text-white/80 sm:text-base" data-id="element-125" data-mp="3dbsoqhefgxr7">
            No estás comprando un simple PDF. Estás incorporando a tu práctica clínica un sistema
            completo de diagnóstico, diseñado para ayudarte a evaluar cada caso con mayor orden,
            criterio y seguridad. Cada recurso cumple una función específica para que puedas pasar de
            la incertidumbre a un proceso clínico claro y repetible.
          </p>
        </div>

        <ul className="mt-8 space-y-4" data-id="element-126" data-mp="19moc92q0mz3x">
          {TOMOS.map((tomo, index) => <motion.li key={tomo.number} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.4,
          delay: Math.min(index * 0.04, 0.2)
        }} className="rounded-3xl border border-navy-100 bg-white p-5 shadow-card" data-id="element-127" data-mp="19osc99ksndyx" data-mpi={index}>
              <div className="flex items-center gap-3" data-id="element-128" data-mp="qttuk110csw0">
                <span className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-2xl bg-navy-800 font-display text-base font-bold text-gold-300" data-id="element-129" data-mp="vu81ffqvhwsg">
                  {tomo.number}
                </span>
                <div className="min-w-0" data-id="element-130" data-mp="lwbuz18vrq0o">
                  <p className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-gold-600" data-id="element-131" data-mp="38jjinzpc0yc">
                    <BookOpenIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-132" data-mp="3ufm9xd7pu37i" />
                    Tomo {tomo.number}
                  </p>
                  <h3 className="font-display text-[17px] font-bold leading-tight text-navy-900 sm:text-xl" data-id="element-133" data-mp="caj05f6t130r">
                    {tomo.title}
                  </h3>
                </div>
              </div>
              <p className="mt-3 text-[14.5px] leading-relaxed text-navy-600 sm:text-base" data-id="element-134" data-mp="m5kmk39p3qzn">
                {tomo.description}
              </p>
              {tomo.bullets ? <ul className="mt-3.5 space-y-2 border-t border-dashed border-navy-100 pt-3.5" data-id="element-135" data-mp="uyfooo3v96l7">
                  {tomo.bullets.map(bullet => <li key={bullet} className="flex items-start gap-2" data-id="element-136" data-mp="14xws0yvnb8e0">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" data-id="element-137" data-mp="14vspak9hgffb" />
                      <span className="text-[13.5px] leading-snug text-navy-700 sm:text-[14.5px]" data-id="element-138" data-mp="3ts3bsp86awvu">
                        {bullet}
                      </span>
                    </li>)}
                </ul> : null}
            </motion.li>)}
        </ul>

        <div className="mt-8 rounded-3xl border-l-4 border-gold-500 bg-white p-5 shadow-card sm:p-6" data-id="element-139" data-mp="bdkjmzcjgx6z">
          <p className="font-display text-[18px] font-semibold leading-snug text-navy-900 sm:text-xl" data-id="element-140" data-mp="gb40m70mfo2m">
            Cada consulta puede convertirse en una oportunidad para aplicar el Sistema.
          </p>
          <p className="mt-3 text-[14.5px] leading-relaxed text-navy-600 sm:text-base" data-id="element-141" data-mp="pd3ewrk94e1v">
            Desde el primer paciente comenzarás a trabajar con un proceso mucho más ordenado. No
            tendrás que improvisar. No tendrás que confiar únicamente en la memoria. Y no volverás a
            preguntarte si olvidaste evaluar algún aspecto importante antes de iniciar un
            tratamiento.
          </p>
        </div>

        <TransformCta headline="Aplica los 6 tomos en tu próxima consulta, no el próximo año." label="Quiero los 6 tomos clínicos" location="contenido" data-id="element-142" data-mp="2olbu1m9x3y3b" />
      </div>
    </section>;
}